import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppDialog from '@components/app-dialog/app-dialog.tsx';
import MediaLibraryContent from '@components/media-library-component/components/media-library-content/media-library-content.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { ImageUploadIcon } from '@components/icons/svg-icon.tsx';
import MediaPreview from '@components/media-library-component/components/media-preview/media-preview.tsx';
import {
  FileTypeEnum,
  MediaLibraryComponentProps
} from '@models/media-library.model.ts';
import { getAboutFileInfo } from '@api/file-upload-api.ts';
import { Skeleton } from '@mui/material';

const MediaLibraryComponent: React.FC<MediaLibraryComponentProps> = ({
  trigger,
  title = 'upload_image_content_text',
  allowedTypes = [FileTypeEnum.Image],
  onSelect,
  mediaKey,
  path,
  width = 174,
  height = 174,
  icon,
  disableDelete
}) => {
  const { t } = useTranslation();
  const {
    selectedMedia,
    resetSelectedMedia,
    resetMediaLibrary,
    setSelectedMedia
  } = useMediaLibraryStore();

  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchMediaInfo = async () => {
      if (!path) return;
      setLoading(true);

      try {
        const mediaInfo = await getAboutFileInfo(path);

        setSelectedMedia(mediaKey, {
          _id: mediaInfo._id || '',
          name: mediaInfo.name || '',
          folder: mediaInfo.folder || null,
          isPrivate: mediaInfo.isPrivate || false,
          size: mediaInfo.size || 0,
          path: mediaInfo.path || path,
          mimetype: mediaInfo.mimetype || '',
          type: mediaInfo.type || FileTypeEnum.Image,
          organizationId: mediaInfo.organizationId || 0,
          createdAt: mediaInfo.createdAt || '',
          updatedAt: mediaInfo.updatedAt || '',
          __v: mediaInfo.__v || 0
        });
      } catch (error) {
        setSelectedMedia(mediaKey, {
          _id: '',
          name: '',
          folder: null,
          isPrivate: false,
          size: 0,
          path: path,
          mimetype: '',
          type: FileTypeEnum.Image,
          organizationId: 0,
          createdAt: '',
          updatedAt: '',
          __v: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMediaInfo();
  }, [path, mediaKey, setSelectedMedia]);

  // The thumbnail is driven by the global selectedMedia slot, which the fetch
  // above only populates for a truthy `path`. When `path` clears (e.g. a
  // discard/revert on the parent), drop the slot so the previously selected
  // media stops showing.
  useEffect(() => {
    if (!path) {
      resetSelectedMedia(mediaKey);
    }
  }, [path, mediaKey, resetSelectedMedia]);

  const handleOpen = () => {
    resetMediaLibrary();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    resetMediaLibrary();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetSelectedMedia(mediaKey);
    onSelect?.(null);
  };

  useEffect(() => {
    const media = selectedMedia[mediaKey];
    if (media) {
      setOpenDialog(false);
      onSelect?.(media);
    }
  }, [selectedMedia?.[mediaKey]]);

  const defaultTrigger = (
    <div
      className={`
      media-default-trigger
      !box-border border-2 rounded-[10px] flex items-center justify-center cursor-pointer relative overflow-hidden
      transition-all duration-100 ease-in-out
      ${
        selectedMedia[mediaKey]
          ? 'border-solid border-[var(--primary)] bg-white'
          : 'border-dashed border-[var(--gray-6)] bg-[var(--gray-11)] hover:bg-white hover:border-[var(--primary)]'
      }
    `}
      style={{ width, height }}
      onClick={handleOpen}
    >
      {loading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ borderRadius: '10px' }}
        />
      ) : !selectedMedia[mediaKey] ? (
        <div className="w-full flex flex-col justify-center items-center gap-1">
          {icon || <ImageUploadIcon />}

          <p className="text-[14px] text-[var(--blue-4)] opacity-50">
            {t(title)}
          </p>
        </div>
      ) : (
        <MediaPreview
          media={selectedMedia[mediaKey]}
          onRemove={handleRemove}
          t={t}
          disableDelete={disableDelete}
        />
      )}
    </div>
  );

  return (
    <div>
      <div
        className="flex"
        style={{ width: width, height: height }}
        onClick={handleOpen}
      >
        {trigger || defaultTrigger}
      </div>

      <AppDialog
        open={openDialog}
        size={'xl'}
        title={t('media_library_dialog_title')}
        content={
          <MediaLibraryContent
            mediaKey={mediaKey}
            allowedTypes={allowedTypes}
            isDialogOpen={openDialog}
          />
        }
        onClose={handleClose}
      />
    </div>
  );
};

export default MediaLibraryComponent;
